import { CSSProperties, FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { ListSourceType } from "@/enum/list.enum";

import { AnalyticsExportButton } from "./components/AnalyticsExportButton";
import { ANALYTICS_TABS, AnalyticsTab } from "./config/tabs";
import { useAnalyticsSelection } from "./hooks/useAnalyticsSelection";
import { ANALYTICS_HEADER_HEIGHT_VAR, useStickyHeaderHeight } from "./hooks/useStickyHeaderHeight";
import { CampaignDeepDiveTab } from "./tabs/CampaignDeepDiveTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { SignalsTab } from "./tabs/SignalsTab";
import { TopCampaignsTab } from "./tabs/TopCampaignsTab";
import { UserDeepDiveTab } from "./tabs/UserDeepDiveTab";
import { ANALYTICS_TAB_PARAM_KEY, normalizeAnalyticsTab } from "./utils/analyticsTab";

/**
 * Analytics shell: owns the tab selection and the report selection (lists, campaigns,
 * date range, drill-down filters), and hands both to whichever view is active. The
 * selection lives here rather than in a tab so switching views never resets it.
 */
export const Analytics: FunctionComponent = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	const activeTab = useMemo(
		() => normalizeAnalyticsTab(searchParams.get(ANALYTICS_TAB_PARAM_KEY)),
		[searchParams]
	);

	// Signals only exist for tracked lists, so that tab offers only those: picking an
	// untracked list there can never report anything but zeros.
	const selection = useAnalyticsSelection({
		listSourceType: activeTab === AnalyticsTab.SIGNALS ? ListSourceType.TRACKER : undefined,
	});

	const handleTabChange = useCallback(
		(value: string) => {
			const next = new URLSearchParams(searchParams);

			next.set(ANALYTICS_TAB_PARAM_KEY, value);
			setSearchParams(next, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	const tabs = useMemo(
		() => ANALYTICS_TABS.map((tab) => ({ label: t(tab.labelKey), value: tab.value })),
		[t]
	);

	const stickyHeader = useStickyHeaderHeight();

	// Published for the per-tab filter bar, which pins directly under the header (PREC-4029).
	const rootStyle = useMemo(
		() => ({ [ANALYTICS_HEADER_HEIGHT_VAR]: `${stickyHeader.height}px` }) as CSSProperties,
		[stickyHeader.height]
	);

	return (
		<div className="flex flex-col items-start w-full" style={rootStyle}>
			{/* The header stays pinned to the top of the outlet scroll container while the report
			    scrolls underneath (PREC-4029), like the Feed page's top nav. -top-3 / -mt-3 / pt-3:
			    sticky insets resolve against the scroll container's content box, so a plain top-0
			    would pin 12px down (the outlet's pt-3) and let rows bleed through that strip; the
			    wrapper cancels the padding itself and hands it back to PageHeader's own -mt-3.
			    bg-white so rows can't show through the pinned bar. */}
			<div
				ref={stickyHeader.ref}
				className="sticky -top-3 -mt-3 z-40 w-full bg-white pt-3"
				data-testid="analytics-sticky-header"
			>
				<PageHeader
					actions={<AnalyticsExportButton payload={selection.payload} />}
					title={t("analytics.title")}
					// A single-view strip is noise; it appears once a second view lands.
					{...(tabs.length > 1
						? { tabs, activeTab: activeTab as string, onTabChange: handleTabChange }
						: {})}
				/>
			</div>

			{activeTab === AnalyticsTab.OVERVIEW ? <OverviewTab selection={selection} /> : null}

			{activeTab === AnalyticsTab.TOP_CAMPAIGNS ? <TopCampaignsTab selection={selection} /> : null}

			{activeTab === AnalyticsTab.CAMPAIGN_DEEP_DIVE ? (
				<CampaignDeepDiveTab selection={selection} />
			) : null}

			{activeTab === AnalyticsTab.USER_DEEP_DIVE ? <UserDeepDiveTab selection={selection} /> : null}

			{activeTab === AnalyticsTab.SIGNALS ? <SignalsTab selection={selection} /> : null}
		</div>
	);
};
